/**
 * Hook to track sync status with storage node
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getGlobalSyncState, onSyncStateChange, syncCircuitBreaker } from '@yada/sync';
import { getStorageUrl } from '@yada/api-client';
import { startVisibleInterval } from '../utils/visibility';

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline' | 'local';

export interface SyncStatusState {
  status: SyncStatus;
  errorMessage?: string;
}

/**
 * Hook to get current sync status
 */
export function useSyncStatus(): SyncStatusState {
  const [status, setStatus] = useState<SyncStatusState>({ status: 'idle' });
  const abortControllerRef = useRef<AbortController | null>(null);
  const sessionExpiredRef = useRef(false);

  const checkSyncStatus = useCallback(async () => {
    if (sessionExpiredRef.current) {
      setStatus({ status: 'offline', errorMessage: 'Session expired. Working offline — sign in to sync.' });
      return;
    }

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Get storage node URL
    const storageUrl = getStorageUrl();

    if (!storageUrl) {
      console.warn('[useSyncStatus] No storage URL configured');
      setStatus({ status: 'offline' });
      return;
    }

    // If circuit breaker is OPEN, do not hammer healthz — report offline directly
    if (syncCircuitBreaker.isOpen()) {
      setStatus({ status: 'offline', errorMessage: 'Sync service cooling off' });
      return;
    }

    try {
      // Try to ping storage node health endpoint with a short timeout
      const response = await fetch(`${storageUrl}/healthz`, {
        method: 'GET',
        signal: abortControllerRef.current.signal,
        cache: 'no-cache',
      });

      if (response.ok) {
        const state = getGlobalSyncState();
        if (state.error) {
          const isOfflineState = state.error.reason === 'offline' || state.error.reason === 'auth';
          setStatus({
            status: isOfflineState ? 'offline' : 'error',
            errorMessage: state.error.reason === 'auth'
              ? 'Session expired. Working offline — sign in to sync.'
              : `Document sync failed: ${state.error.reason}`,
          });
        } else if (state.hasSyncing || state.hasPending) {
          setStatus({ status: 'syncing' });
        } else {
          setStatus({ status: 'idle' });
        }
      } else {
        console.warn('[useSyncStatus] Storage node returned error status:', response.status, storageUrl);
        syncCircuitBreaker.recordFailure(response.status >= 500);
        setStatus({ status: 'error', errorMessage: `Storage node returned ${response.status}` });
      }
    } catch (err) {
      // Don't log aborted requests
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      // Storage node unreachable
      syncCircuitBreaker.recordFailure(true);
      console.warn('[useSyncStatus] Storage node unreachable:', storageUrl, err);
      setStatus({ status: 'offline' });
    }
  }, []);

  useEffect(() => onSyncStateChange((state) => {
    if (state.error) {
      const isOfflineState = state.error.reason === 'offline' || state.error.reason === 'auth';
      setStatus({
        status: isOfflineState ? 'offline' : 'error',
        errorMessage: state.error.reason === 'auth'
          ? 'Session expired. Working offline — sign in to sync.'
          : `Document sync failed: ${state.error.reason}`,
      });
    } else if (!sessionExpiredRef.current) {
      setStatus({ status: state.hasSyncing || state.hasPending ? 'syncing' : 'idle' });
    }
  }), []);

  useEffect(() => {
    const handleSessionExpired = () => {
      sessionExpiredRef.current = true;
      setStatus({ status: 'offline', errorMessage: 'Session expired. Working offline — sign in to sync.' });
    };
    const handleAccountChanged = () => {
      if (localStorage.getItem('yada:access_token')) {
        sessionExpiredRef.current = false;
        void checkSyncStatus();
      }
    };
    window.addEventListener('yada:session-expired', handleSessionExpired);
    window.addEventListener('yada:account:changed', handleAccountChanged);
    return () => {
      window.removeEventListener('yada:session-expired', handleSessionExpired);
      window.removeEventListener('yada:account:changed', handleAccountChanged);
    };
  }, [checkSyncStatus]);

  // Initialize: check immediately on mount
  useEffect(() => {
    checkSyncStatus();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [checkSyncStatus]);

  // Check once a minute while connected and every 10 s while offline, only
  // while the tab is visible; returning to the tab checks immediately.
  useEffect(() => {
    const intervalMs = status.status !== 'offline' ? 60000 : 10000;
    return startVisibleInterval(() => { void checkSyncStatus(); }, intervalMs);
  }, [status.status, checkSyncStatus]);

  return status;
}
